// hooks/useWorksLoading.js
import { useState, useCallback } from 'react';

export const useWorksLoading = () => {
    const [loadingProjects, setLoadingProjects] = useState(new Set());
    const [loadedProjects, setLoadedProjects] = useState(new Set());
    const [loadingProgress, setLoadingProgress] = useState({});

    const loadResourceWithTimeout = (resource, timeout = 5000) => {
        return new Promise((resolve) => {
            const timeoutId = setTimeout(() => resolve(false), timeout);

            try {
                if (resource.type === 'image') {
                    const img = new Image();
                    img.onload = () => {
                        clearTimeout(timeoutId);
                        resolve(true);
                    };
                    img.onerror = () => {
                        clearTimeout(timeoutId);
                        resolve(false);
                    };
                    img.src = resource.url;
                } else if (resource.type === 'video') {
                    // Skip video loading on mobile for performance
                    if (window.innerWidth <= 768) {
                        clearTimeout(timeoutId);
                        resolve(true);
                        return;
                    }

                    const video = document.createElement('video');
                    video.oncanplay = () => {
                        clearTimeout(timeoutId);
                        resolve(true);
                    };
                    video.onerror = () => {
                        clearTimeout(timeoutId);
                        resolve(false);
                    };
                    video.preload = 'metadata';
                    video.src = resource.url;
                } else {
                    clearTimeout(timeoutId);
                    resolve(true);
                }
            } catch (error) {
                clearTimeout(timeoutId);
                resolve(false);
            }
        });
    };

    const loadProject = useCallback(async (project) => {
        if (loadedProjects.has(project.id) || loadingProjects.has(project.id)) {
            return Promise.resolve();
        }

        setLoadingProjects(prev => new Set(prev).add(project.id));
        setLoadingProgress(prev => ({ ...prev, [project.id]: 0 }));

        try {
            const resourcesToLoad = [];
            const isMobile = window.innerWidth <= 768;

            // Collect project resources with validation
            if (project.mainImage && (project.mainImage.startsWith('http') || project.mainImage.startsWith('/'))) {
                resourcesToLoad.push({ type: 'image', url: project.mainImage });
            }

            // Skip video on mobile or if URL is invalid
            if (!isMobile && project.videoUrl && (project.videoUrl.startsWith('http') || project.videoUrl.startsWith('/'))) {
                resourcesToLoad.push({ type: 'video', url: project.videoUrl });
            }

            // Only load first few gallery images on mobile
            if (project.gallery && Array.isArray(project.gallery)) {
                const galleryLimit = isMobile ? 3 : project.gallery.length;
                project.gallery.slice(0, galleryLimit).forEach(item => {
                    if (item.url && (item.url.startsWith('http') || item.url.startsWith('/'))) {
                        resourcesToLoad.push({ type: item.type || 'image', url: item.url });
                    }
                });
            }

            // If no resources to load, mark as loaded immediately
            if (resourcesToLoad.length === 0) {
                setLoadedProjects(prev => new Set(prev).add(project.id));
                setLoadingProjects(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(project.id);
                    return newSet;
                });
                setLoadingProgress(prev => ({ ...prev, [project.id]: 100 }));
                return;
            }

            // Load all resources in parallel with progress tracking
            const results = await Promise.allSettled(
                resourcesToLoad.map(resource => loadResourceWithTimeout(resource, 3000))
            );
            results.forEach((result, i) => {
                const progress = Math.round(((i + 1) / resourcesToLoad.length) * 100);
                setLoadingProgress(prev => ({ ...prev, [project.id]: progress }));
            });

            // Mark as loaded even if some resources failed
            setLoadedProjects(prev => new Set(prev).add(project.id));
            setLoadingProjects(prev => {
                const newSet = new Set(prev);
                newSet.delete(project.id);
                return newSet;
            });

        } catch (error) {
            console.warn(`Project loading error for ${project.id}:`, error);
            // Mark as loaded anyway to prevent infinite loading
            setLoadedProjects(prev => new Set(prev).add(project.id));
            setLoadingProjects(prev => {
                const newSet = new Set(prev);
                newSet.delete(project.id);
                return newSet;
            });
            setLoadingProgress(prev => ({ ...prev, [project.id]: 100 }));
        }
    }, [loadedProjects, loadingProjects]);

    const loadMultipleProjects = useCallback(async (projects) => {
        const loadPromises = projects.map(project =>
            loadProject(project).catch(error => {
                console.warn(`Failed to load project ${project.id}:`, error);
                return null;
            })
        );
        await Promise.allSettled(loadPromises);
    }, [loadProject]);

    const isProjectLoaded = useCallback((projectId) => {
        return loadedProjects.has(projectId);
    }, [loadedProjects]);

    const isProjectLoading = useCallback((projectId) => {
        return loadingProjects.has(projectId);
    }, [loadingProjects]);

    const getProjectProgress = useCallback((projectId) => {
        return loadingProgress[projectId] || 0;
    }, [loadingProgress]);

    return {
        loadProject,
        loadMultipleProjects,
        isProjectLoaded,
        isProjectLoading,
        getProjectProgress,
        loadedProjects: Array.from(loadedProjects),
        loadingProjects: Array.from(loadingProjects)
    };
};